<template>
  <div class="step-picker">
    <div class="sp-search" :class="{ 'is-search-only': !creatable }">
      <div class="input-group">
        <span class="input-group-addon"><fa-icon :icon="['fal', 'search']"/></span>
        <input
          ref="input"
          type="text"
          class="form-input"
          :placeholder="placeholder"
          v-model="query"
          autocomplete="nope"
          @keydown.enter.prevent="onEnter"
        >
      </div>
      <button
        class="btn btn-secondary btn-icon btn-icon-left"
        v-if="creatable"
        :disabled="creating"
        @click="$emit('create', query.trim())"
      >
        <fa-icon :icon="['fal', 'plus']"/>Cadastrar {{ createLabel }}
      </button>
    </div>

    <slot name="create"/>

    <template v-if="!creating">
      <div class="loading loading-lg mt-2" v-if="loading"/>

      <div class="empty sp-empty" v-else-if="isEmptyBase">
        <div class="empty-icon"><fa-icon :icon="['fal', 'plus-circle']" size="2x"/></div>
        <p class="empty-title">{{ emptyTitle }}</p>
        <p class="empty-subtitle">{{ emptyHint }}</p>
        <button class="btn btn-secondary btn-sm" v-if="creatable" @click="$emit('create', '')">
          Cadastrar {{ createLabel }}
        </button>
      </div>

      <div class="sp-results" v-else-if="isOpen">
        <button
          v-for="item in visible"
          :key="item.id"
          class="sp-result"
          :class="{ picked: isPicked(item.id) }"
          @click="togglePick(item.id)"
        >
          <span class="sp-avatar" :class="{ square: item.square }">{{ item.avatar }}</span>
          <span class="sp-text">
            <span class="sp-title">{{ item.title }}</span>
            <span class="sp-meta" :class="{ 'text-warning': item.warning }">{{ item.meta }}</span>
          </span>
          <span class="sp-check" :class="{ on: isPicked(item.id) }"/>
        </button>

        <button
          class="sp-result sp-create"
          v-if="canCreateFromQuery"
          @click="$emit('create', query.trim())"
        >
          <span class="sp-avatar"><fa-icon :icon="['fal', 'plus']"/></span>
          <span class="sp-text">
            <span class="sp-title">Cadastrar “{{ query.trim() }}”</span>
            <span class="sp-meta">{{ createHint }}</span>
          </span>
          <span class="sp-added text-primary">Novo</span>
        </button>

        <div class="sp-none" v-if="visible.length === 0 && !canCreateFromQuery">
          {{ allAdded ? 'Todos já estão neste horário' : noneLabel }}
        </div>

        <slot name="results-footer"/>

        <div class="sp-pickbar" v-if="available.length > 0">
          <a href="#" @click.prevent="pickAll">
            {{ allPicked ? 'Limpar seleção' : `Selecionar todos (${available.length})` }}
          </a>
          <span class="sp-pickbar-right">
            <span class="text-gray">
              <strong>{{ picked.length }}</strong>
              {{ picked.length === 1 ? 'selecionado' : 'selecionados' }}
            </span>
            <button
              class="btn btn-sm btn-primary btn-icon"
              :disabled="picked.length === 0"
              @click="addPicked"
            >{{ addLabel }}<fa-icon class="ml-2" :icon="['fal', 'arrow-right']"/></button>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export default {
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Buscar...',
    },
    createLabel: {
      type: String,
      default: '',
    },
    creatable: {
      type: Boolean,
      default: true,
    },
    addLabel: {
      type: String,
      default: 'Adicionar',
    },
    createHint: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    creating: {
      type: Boolean,
      default: false,
    },
    remote: {
      type: Boolean,
      default: false,
    },
    hasAny: {
      type: Boolean,
      default: true,
    },
    emptyTitle: {
      type: String,
      default: 'Nenhum registro cadastrado ainda',
    },
    emptyHint: {
      type: String,
      default: '',
    },
    noneLabel: {
      type: String,
      default: 'Nenhum resultado',
    },
  },
  data() {
    return {
      query: '',
      picked: [],
      debounce: null,
    };
  },
  computed: {
    isOpen() {
      return true;
    },
    isEmptyBase() {
      return !this.hasAny;
    },
    visible() {
      if (this.remote) return this.items.filter(item => !this.isSelected(item.id));
      const query = normalize(this.query.trim());
      return this.items.filter(item => (
        !this.isSelected(item.id)
        && (!query || normalize(item.search || item.title).includes(query))
      ));
    },
    allAdded() {
      return !this.query.trim()
        && this.items.length > 0
        && this.items.every(item => this.isSelected(item.id));
    },
    available() {
      return this.visible
        .map(({ id }) => id)
        .filter(id => !this.isSelected(id));
    },
    allPicked() {
      return this.available.length > 0 && this.available.every(id => this.isPicked(id));
    },
    canCreateFromQuery() {
      if (!this.creatable) return false;
      const query = normalize(this.query.trim());
      if (!query) return false;
      return !this.items.some(item => normalize(item.title) === query);
    },
  },
  watch: {
    query() {
      if (!this.remote) return;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(this.emitSearch, 500);
    },
    creating(value) {
      if (value) return;
      this.query = '';
      this.focus();
    },
  },
  mounted() {
    this.focus();
  },
  methods: {
    emitSearch() {
      this.$emit('search', { query: this.query.trim() });
    },
    focus() {
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus();
      });
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    isPicked(id) {
      return this.picked.includes(id);
    },
    togglePick(id) {
      if (this.isSelected(id)) return;
      if (this.isPicked(id)) {
        this.picked = this.picked.filter(item => item !== id);
      } else {
        this.picked.push(id);
      }
    },
    pickAll() {
      this.picked = this.allPicked ? [] : [...this.available];
    },
    addPicked() {
      if (this.picked.length === 0) return;
      this.$emit('add', [...this.picked]);
      this.reset();
    },
    onEnter() {
      if (this.picked.length > 0) {
        this.addPicked();
        return;
      }
      const first = this.available[0];
      if (first) {
        this.$emit('add', [first]);
        this.reset();
      } else if (this.canCreateFromQuery) {
        this.$emit('create', this.query.trim());
      }
    },
    reset() {
      this.query = '';
      this.picked = [];
      this.focus();
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';

.step-picker {
  .sp-search {
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    &.is-search-only {
      grid-template-columns: minmax(0, 1fr);
    }
    .input-group-addon {
      color: $secondary-color;
    }
  }
  .sp-empty {
    background-color: $gray-color-ultra-light;
    border: $border-width dashed $border-color-dark;
    border-radius: $border-radius;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: $layout-spacing;
    min-height: 9.8rem;
    padding: $layout-spacing-lg;
    .empty-icon {
      color: lighten($primary-color, 20%);
      margin-bottom: $layout-spacing-sm;
    }
    .empty-title {
      color: $secondary-color-dark;
      font-size: $font-size-sm;
      font-weight: 700;
      margin-bottom: $layout-spacing-sm;
    }
    .empty-subtitle {
      color: $gray-color-dark;
      font-size: $font-size-xs;
      margin-bottom: $layout-spacing;
    }
  }
  .sp-results {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    margin-top: $layout-spacing;
    max-height: 17.5rem;
    overflow: auto;
  }
  .sp-result {
    align-items: center;
    background: none;
    border: 0;
    border-bottom: $border-width solid $border-color;
    color: $body-font-color;
    cursor: pointer;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: 1.6rem minmax(0, 1fr) auto;
    padding: $layout-spacing $layout-spacing-lg;
    text-align: left;
    width: 100%;
    &:last-of-type {
      border-bottom: 0;
    }
    &:hover, &:focus {
      background-color: lighten($primary-color, 36%);
      outline: 0;
    }
    &.picked {
      background-color: lighten($primary-color, 33%);
    }
    &.sp-create {
      background-color: $gray-color-ultra-light;
      .sp-avatar {
        background-color: $primary-color;
        color: $light-color;
      }
      .sp-title {
        color: $primary-color;
      }
    }
  }
  .sp-avatar {
    align-items: center;
    background-color: lighten($info-color, 48%);
    border-radius: 50%;
    color: $secondary-color;
    display: flex;
    font-size: $font-size-xs;
    font-weight: 600;
    height: 1.5rem;
    justify-content: center;
    width: 1.5rem;
    &.square {
      border-radius: $border-radius;
    }
  }
  .sp-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .sp-title {
    color: $gray-color-900;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sp-meta {
    color: $gray-color-dark;
    font-size: $font-size-xs;
  }
  .sp-added {
    font-size: $font-size-xs;
    font-weight: 600;
    white-space: nowrap;
  }
  .sp-check {
    background-color: $light-color;
    border: $border-width solid $border-color-dark;
    border-radius: $border-radius;
    display: inline-block;
    height: .9rem;
    position: relative;
    width: .9rem;
    &.on {
      background-color: $primary-color;
      border-color: $primary-color;
      &::after {
        border: solid $light-color;
        border-width: 0 2px 2px 0;
        content: '';
        height: .5rem;
        left: .28rem;
        position: absolute;
        top: .06rem;
        transform: rotate(45deg);
        width: .25rem;
      }
    }
  }
  .sp-none {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    padding: $layout-spacing-lg;
    text-align: center;
  }
  .sp-pickbar {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border-top: $border-width solid $border-color;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
    position: sticky;
    .sp-pickbar-right {
      align-items: center;
      display: flex;
      gap: $layout-spacing;
    }
  }
  @media screen and (max-width: $size-sm) {
    .sp-search {
      grid-template-columns: 1fr;
    }
  }
}
</style>
